// World system (owner: world): builds one room from its template — tilemap,
// parallax, ambience, set dressing, props — and runs the room director.
// Also guards the player against hazards and falling out of the world.

import type Phaser from 'phaser';
import { PLAYER_BODY, TILE } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { GameContext, Vec, WorldApi } from '../../core/contracts';
import type { SectorDef } from '../../core/sector';
import type { LoopHandle } from '../../core/contracts';
import type { RouteNode } from '../../core/types';
import { ROOMS_BY_ID, roomsOfType, type RoomTemplate } from '../../data/rooms';
import { Ambient } from './ambient';
import { RoomDirector } from './director';
import { Destructible } from './destructible';
import { Dressing } from './dressing';
import { Parallax } from './parallax';
import { EdgeDoors, ExitGate } from './props';
import { CELL, parseRoom, type RoomLayout } from './template';
import { RoomMap } from './tilemap';

/** Hazard contact damage and launch. */
const HAZARD_DAMAGE = 12;
const HAZARD_KNOCKBACK = 760;
const HAZARD_RECHECK = 0.12;
/** Penalty for falling out of the world. */
const FALL_DAMAGE = 10;
/** Highest the player may rise above the top edge of the room. */
const CEILING_MARGIN = 160;

const HAZARD_TAGS: Record<SectorDef['hazard'], ('electric' | 'burn' | 'gravity')[]> = {
  electric: ['electric'],
  molten: ['burn'],
  void: ['gravity'],
};

function resolveLayout(node: RouteNode): RoomLayout {
  const template = ROOMS_BY_ID[node.roomId] ?? roomsOfType(node.type)[0] ?? roomsOfType('combat')[0];
  if (!ROOMS_BY_ID[node.roomId]) console.warn(`[world] unknown room "${node.roomId}", using ${template.id}`);
  return parseRoom(template);
}

/** Versus arenas: a given template, a chosen spawn marker, and no room director (no exit, waves or challenges). */
export interface WorldOptions {
  template?: RoomTemplate;
  /** Index into the template's P markers (versus: one per side). */
  spawnIndex?: number;
  versus?: boolean;
}

export function createWorld(ctx: GameContext, node: RouteNode, sector: SectorDef, opts: WorldOptions = {}): WorldApi {
  const scene = ctx.scene;
  const layout = opts.template ? parseRoom(opts.template) : resolveLayout(node);
  const map = new RoomMap(scene, layout, sector);
  const parallax = new Parallax(scene, sector, map.height);
  const dressing = new Dressing(scene, sector, map);
  const ambient = new Ambient(scene, sector, map);
  const doors = new EdgeDoors(scene, map.doors);
  const terrain = new Destructible(ctx, map);

  const feet = (m: { cx: number; ty: number }): Vec => ({ x: m.cx * TILE, y: (m.ty + 1) * TILE });
  const ground = layout.markers.g.map(feet);
  const air = layout.markers.a.map((m) => ({ x: m.cx * TILE, y: (m.ty + 0.5) * TILE }));
  const spawns = layout.markers.P;
  const p = spawns[Math.min(spawns.length - 1, Math.max(0, opts.spawnIndex ?? 0))] ?? { cx: 3.5, ty: layout.h - 4 };
  const spawnFeet = feet(p);
  /** Player spawn = body centre standing on the P surface. */
  const playerSpawn: Vec = { x: spawnFeet.x, y: spawnFeet.y - PLAYER_BODY.h / 2 - 2 };
  const x = layout.markers.X[0];
  const exitFeet = x ? feet(x) : { x: map.width - 3 * TILE, y: map.height - 3 * TILE };
  const exit = opts.versus ? null : new ExitGate(scene, exitFeet.x, exitFeet.y, COLORS.volt, 'ROUTE');

  const director = exit ? new RoomDirector(ctx, { node, sector, layout, map, ground, air, exit, doors }) : null;

  // Physics bounds: walls are tiles; the bounds only stop bodies that opt into them.
  scene.physics.world.setBounds(0, -CEILING_MARGIN * 4, map.width, map.height + CEILING_MARGIN * 8);

  let destroyed = false;
  let rainLoop: LoopHandle | null = null;
  let hazardTimer = 0;
  const safe = { x: playerSpawn.x, feetY: spawnFeet.y, timer: 0 };

  /** Hazard contact: any hazard cell touching the player's body (feet / sides). */
  function checkHazards(dt: number): void {
    hazardTimer -= dt;
    if (hazardTimer > 0) return;
    const pl = ctx.player;
    if (!pl.alive) return;
    const b = pl.body;
    const x0 = Math.floor((b.x - 2) / TILE);
    const x1 = Math.floor((b.right + 2) / TILE);
    const y0 = Math.floor(b.y / TILE);
    const y1 = Math.floor((b.bottom + 2) / TILE);
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        if (map.kindAtTile(tx, ty) !== CELL.HAZARD) continue;
        hazardTimer = HAZARD_RECHECK;
        const res = ctx.damage.hitPlayer({
          amount: HAZARD_DAMAGE,
          tags: [...HAZARD_TAGS[sector.hazard]],
          source: 'hazard',
          sourceX: pl.x,
          sourceY: (ty + 1) * TILE,
          knockback: HAZARD_KNOCKBACK,
          kbAngle: -Math.PI / 2,
          label: 'hazard',
        });
        if (!res.blocked && res.dealt > 0) {
          try {
            ctx.fx.sparks(pl.x, ty * TILE, -Math.PI / 2, { count: 12, color: COLORS.threat, spread: Math.PI * 0.8 });
          } catch {
            // Cosmetic only.
          }
        }
        return;
      }
    }
  }

  /** Remember the last solid footing; recover the player if they leave the world. */
  function checkFallAndBounds(dt: number): void {
    const pl = ctx.player;
    if (!pl.alive) return;
    const b = pl.body;
    safe.timer -= dt;
    if (pl.onGround && safe.timer <= 0) {
      const under = map.kindAt(pl.x, pl.feetY + 4);
      const left = map.kindAt(b.x + 2, pl.feetY + 4);
      const right = map.kindAt(b.right - 2, pl.feetY + 4);
      const footing = (k: number) => k === CELL.SOLID || k === CELL.ONEWAY;
      if (footing(under) && footing(left) && footing(right)) {
        safe.x = pl.x;
        safe.feetY = pl.feetY;
        safe.timer = 0.2;
      }
    }
    if (b.y > map.height + TILE * 2 || b.x < -TILE || b.right > map.width + TILE) {
      b.reset(safe.x, safe.feetY - b.height / 2 - 2);
      ctx.damage.hitPlayer({ amount: FALL_DAMAGE, tags: [], source: 'hazard', ignoreIFrames: true, label: 'fall' });
      try {
        ctx.fx.ring(safe.x, safe.feetY - b.height / 2, 90, { color: COLORS.volt, duration: 400 });
      } catch {
        // Cosmetic only.
      }
      return;
    }
    if (b.y < -CEILING_MARGIN && b.velocity.y < 0) b.velocity.y = 0;
  }

  const api: WorldApi = {
    node,
    sector,
    roomId: layout.id,
    width: map.width,
    height: map.height,
    solids: map.solids,
    playerSpawn,
    get state() {
      return director?.state ?? 'cleared';
    },
    get challenge() {
      return director?.challenge?.state ?? null;
    },

    start() {
      director?.start();
      if (sector.ambient === 'rain') {
        try {
          rainLoop = ctx.audio.loop('rain', { volume: 0.18 + 0.32 * map.skyOpenness });
        } catch (err) {
          console.warn('[world] rain loop failed', err);
        }
      }
    },

    update(dt: number) {
      if (destroyed) return;
      const cam = scene.cameras.main;
      parallax.update(cam);
      ambient.update(dt, cam);
      dressing.update(dt, cam);
      terrain.update(dt);
      director?.update(dt);
      checkHazards(dt);
      checkFallAndBounds(dt);
    },

    destroy() {
      if (destroyed) return;
      destroyed = true;
      rainLoop?.stop(400);
      rainLoop = null;
      for (const part of [director, exit, doors, ambient, dressing, terrain, parallax, map].filter(Boolean) as { destroy(): void }[]) {
        try {
          part.destroy();
        } catch {
          // Scene shutdown already destroyed the display list; nothing left to free.
        }
      }
    },

    addCollider(obj: Phaser.GameObjects.GameObject, opts?: { canDrop?: () => boolean; onHit?: () => void }) {
      return map.addCollider(obj, opts);
    },
    isSolidAt: (x: number, y: number) => map.isSolidAt(x, y),
    isOneWayAt: (x: number, y: number) => map.isOneWayAt(x, y),
    raycast: (x0: number, y0: number, x1: number, y1: number) => map.raycast(x0, y0, x1, y1),
    groundBelow: (x: number, y: number, maxDist?: number) => map.groundBelow(x, y, maxDist),
    damageTerrain(x: number, y: number, amount: number, radius?: number) {
      if (destroyed) return;
      if (radius !== undefined && radius > 0) terrain.hitRadius(x, y, radius, amount);
      else terrain.hitAt(x, y, amount);
    },
    spawnPoints(kind: 'ground' | 'air' | 'any') {
      if (kind === 'ground') return ground.map((v) => ({ ...v }));
      if (kind === 'air') return air.map((v) => ({ ...v }));
      return [...ground, ...air].map((v) => ({ ...v }));
    },
    lock: () => director?.lock(),
    unlock: () => director?.unlock(),
  };
  return api;
}
